import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "@/components/cre/Header";
import Footer from "@/components/cre/Footer";
import AgentCard from "@/components/cre/AgentCard";
import ConsensusPanel from "@/components/cre/ConsensusPanel";
import RunbookPanel from "@/components/cre/RunbookPanel";
import ApprovalGate from "@/components/cre/ApprovalGate";
import AuditTranscript from "@/components/cre/AuditTranscript";
import MTTRPanel from "@/components/cre/MTTRPanel";
import ComingSoon from "@/components/cre/ComingSoon";
import ExecutiveSummary from "@/components/cre/ExecutiveSummary";
import CREScorePanel from "@/components/cre/CREScorePanel";
import GuardrailPanel from "@/components/cre/GuardrailPanel";
import CollapsibleSection from "@/components/cre/CollapsibleSection";
import ReviewerFeedbackPanel from "@/components/cre/ReviewerFeedbackPanel";
import RootCausePanel from "@/components/cre/RootCausePanel";
import FinalActionPlan from "@/components/cre/FinalActionPlan";
import TriagePanel from "@/components/cre/TriagePanel";
import AccountabilityPanel from "@/components/cre/AccountabilityPanel";
import ApprovalEvidencePackage from "@/components/cre/ApprovalEvidencePackage";
import IncidentFlowView from "@/components/cre/IncidentFlowView";
import PipelineProgress from "@/components/cre/PipelineProgress";
import UpgradeDialog from "@/components/cre/UpgradeDialog";
import PersonaSwitcher from "@/components/cre/PersonaSwitcher";
import LeadershipSummary from "@/components/cre/LeadershipSummary";
import { WarRoomToolbar } from "@/components/cre/warroom/WarRoomToolbar";
import { JumpNav } from "@/components/cre/warroom/JumpNav";
import { GovernanceRails } from "@/components/cre/warroom/GovernanceRails";
import {
  SEVERITY_COLORS,
  STATUS_STYLE,
  showSection,
} from "@/components/cre/warroom/personaConfig";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function WarRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [incident, setIncident] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [loading, setLoading] = useState(true);
  // Controlled open: undefined = each section uses its own state.
  // true = all sections open. false = all collapsed (except CRE Assessment).
  const [globalOpen, setGlobalOpen] = useState(undefined);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState("fullView");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const inc = await api.getIncident(id).catch(() => null);
      if (cancelled) return;
      if (inc) setIncident(inc);
      setLoading(false);
      if (inc && inc.status === "analyzing") {
        setAnalyzing(true);
        const start = Date.now();
        while (!cancelled && Date.now() - start < 240000) {
          await new Promise((r) => setTimeout(r, 3000));
          const fresh = await api.getIncident(id).catch(() => null);
          if (cancelled) return;
          if (fresh) setIncident(fresh);
          if (fresh && fresh.status !== "analyzing") break;
        }
        if (!cancelled) setAnalyzing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    toast.message("Running full multi-agent incident analysis…", {
      description:
        "7 domain agents + safety guardrail + root-cause + runbook synthesizers run in parallel. Full governed analysis may take around 2–3 minutes depending on incident complexity. Final outcome is generated only after every specialist agent completes its reasoning.",
    });
    try {
      const inc = await api.analyze(id, null);
      setIncident(inc);
      if (inc.status === "analyzing") {
        const start = Date.now();
        while (Date.now() - start < 240000) {
          await new Promise((r) => setTimeout(r, 3000));
          try {
            const fresh = await api.getIncident(id);
            setIncident(fresh);
            if (fresh.status !== "analyzing") {
              toast.success("Agents returned — consensus + guardrail computed");
              break;
            }
          } catch (_) {
            /* keep polling */
          }
        }
      } else {
        toast.success("Agents returned — consensus + guardrail computed");
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        const msg =
          (detail && detail.message) ||
          "You have used your monthly CRE reasoning runs. Upgrade to CRE Pro for 30 reasoning runs per month.";
        toast.error(msg);
        setUpgradeOpen(true);
      } else if (status === 401) {
        toast.error("Please sign in to dispatch agents.");
      } else {
        toast.error(
          (typeof detail === "string" && detail) ||
            detail?.message ||
            "Analysis failed"
        );
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const sendDecision = async (payload) => {
    const inc = await api.approve(id, payload);
    setIncident(inc);
  };

  const archive = async () => {
    try {
      await api.archive(id);
      toast.success("Incident archived");
      navigate("/dashboard");
    } catch (_) {
      toast.error("Could not archive");
    }
  };

  if (loading || !incident) {
    return (
      <div className="min-h-screen cre-grid-bg flex flex-col">
        <Header />
        <div className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-8 py-12 text-slate-500">
          Loading incident…
        </div>
        <Footer />
      </div>
    );
  }

  const hasAnalysis = Boolean(incident.consensus && incident.runbook);

  return (
    <div className="min-h-screen cre-dot-bg flex flex-col">
      <Header />
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 md:px-8 py-6 md:py-8 space-y-6 md:space-y-8">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/dashboard"
            data-testid="back-to-dashboard"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
          </Link>
          <WarRoomToolbar
            incident={incident}
            hasAnalysis={hasAnalysis}
            analyzing={analyzing}
            onRunAnalysis={runAnalysis}
            onArchive={archive}
            onIncidentUpdate={(updated) => setIncident(updated)}
          />
        </div>

        {/* Title row */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className={`rounded-sm font-mono ${SEVERITY_COLORS[incident.severity] || ""}`}
              >
                {incident.severity}
              </Badge>
              <span className={`cre-overline ${STATUS_STYLE[incident.status] || ""}`}>
                · {incident.status.replace(/_/g, " ")}
              </span>
              <span className="text-[11px] font-mono text-zinc-600">
                · {incident.source}
              </span>
              {incident.archived ? (
                <Badge
                  variant="outline"
                  className="rounded-sm font-mono uppercase border-white/15 text-zinc-400"
                >
                  archived
                </Badge>
              ) : null}
            </div>
            <h1
              data-testid="incident-title"
              className="mt-2 font-heading text-2xl md:text-3xl font-black tracking-tight"
            >
              {incident.title}
            </h1>
            <div className="mt-1 text-sm text-zinc-500 font-mono">
              {incident.affected_system}
            </div>
          </div>
        </div>

        {/* Persona View — pure presentation-layer filter, does NOT change
            any analysis, AI, RBAC, persistence, or backend logic. */}
        <PersonaSwitcher value={selectedPersona} onChange={setSelectedPersona} />

        {selectedPersona === "leadership" ? (
          <LeadershipSummary incident={incident} />
        ) : null}

        <JumpNav
          persona={selectedPersona}
          onExpandAll={() => setGlobalOpen(true)}
          onCollapseAll={() => setGlobalOpen(false)}
        />

        {/* Incident Flow View — high-level 6-node chain at the very top */}
        {incident.flow_view && showSection(selectedPersona, "sec-flow") ? (
          <div id="sec-flow" className="scroll-mt-32">
            <IncidentFlowView flow={incident.flow_view} />
          </div>
        ) : null}

        {/* Triage Assessment — surfaced right after the flow */}
        {incident.triage && showSection(selectedPersona, "sec-triage") ? (
          <div id="sec-triage" className="scroll-mt-32">
            <TriagePanel triage={incident.triage} />
          </div>
        ) : null}

        {/* Executive summary — always visible at top, default expanded */}
        {showSection(selectedPersona, "sec-summary") ? (
          <div id="sec-summary" className="scroll-mt-32">
            <ExecutiveSummary incident={incident} />
          </div>
        ) : null}

        {/* CRE Score — reasoning readiness scorecard */}
        {(hasAnalysis || incident.cre_score) &&
        showSection(selectedPersona, "sec-cre-score") ? (
          <div id="sec-cre-score" className="scroll-mt-32">
            <CREScorePanel score={incident.cre_score} />
          </div>
        ) : null}

        {/* Pipeline progress strip */}
        {incident.pipeline_progress?.length ? (
          <PipelineProgress stages={incident.pipeline_progress} />
        ) : null}

        {/* Structured root cause analysis */}
        {incident.root_cause_analysis &&
        showSection(selectedPersona, "sec-rootcause") ? (
          <div id="sec-rootcause" className="scroll-mt-32">
            <CollapsibleSection
              overline="Root cause"
              title="Likely failure mechanism — structured analysis"
              testId="section-rootcause"
              controlledOpen={globalOpen}
              defaultOpen={true}
            >
              <RootCausePanel rootCause={incident.root_cause_analysis} />
            </CollapsibleSection>
          </div>
        ) : null}

        {/* Incident summary */}
        {showSection(selectedPersona, "sec-incident") ? (
          <CollapsibleSection
            id="sec-incident"
            overline="Incident"
            title="Incident summary"
            testId="section-incident"
            controlledOpen={globalOpen}
            defaultOpen={true}
          >
            <div className="cre-surface rounded-sm p-5 md:p-6">
              <p className="text-sm text-zinc-300 leading-relaxed">
                {incident.description}
              </p>

              {incident.symptoms?.length ? (
                <div className="mt-4">
                  <div className="cre-overline mb-1.5">Symptoms</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {incident.symptoms.map((s, i) => (
                      <div
                        key={`${s}-${i}`}
                        className="text-xs text-zinc-400 font-mono flex gap-2"
                      >
                        <span className="text-blue-400">›</span>
                        <span>{s}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {incident.telemetry_hints ? (
                <div className="mt-4">
                  <div className="cre-overline mb-1.5">Telemetry hints</div>
                  <div className="cre-terminal rounded-sm p-3 text-xs">
                    {incident.telemetry_hints}
                  </div>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <ComingSoon label="Live telemetry stream" variant="ghost" />
                    <ComingSoon label="CMDB graph" variant="ghost" />
                  </div>
                </div>
              ) : null}
            </div>
          </CollapsibleSection>
        ) : null}

        {/* Loading state */}
        {analyzing && !hasAnalysis ? (
          <div className="cre-surface rounded-sm p-10 text-center">
            <Loader2 className="h-6 w-6 mx-auto text-blue-300 animate-spin" />
            <div className="mt-3 font-heading font-semibold">
              7 domain agents + safety guardrail are reasoning in parallel…
            </div>
            <div className="mt-1 text-xs text-zinc-500 font-mono">
              network · infrastructure · application · journey-routing ·
              contract-validation · security · business · safety
            </div>
          </div>
        ) : null}

        {/* Agent workspace */}
        {incident.hypotheses?.length &&
        showSection(selectedPersona, "sec-agents") ? (
          <div id="sec-agents" className="scroll-mt-32">
            <CollapsibleSection
              overline="Agent workspace"
              title="Multi-Agent RCA Hypotheses"
              testId="section-agents"
              controlledOpen={globalOpen}
              badge={
                <span className="text-[11px] font-mono text-zinc-500 ml-2 hidden md:inline">
                  {incident.hypotheses.length} agents responded
                </span>
              }
            >
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="agent-grid"
              >
                {incident.hypotheses.map((h, i) => (
                  <AgentCard key={h.agent_id} hyp={h} index={i} />
                ))}
              </div>
            </CollapsibleSection>
          </div>
        ) : null}

        {/* Consensus + Guardrail */}
        {hasAnalysis && showSection(selectedPersona, "sec-consensus") ? (
          <div id="sec-consensus" className="scroll-mt-32">
            <CollapsibleSection
              overline="Consensus"
              title="Weighted consensus & safety review"
              testId="section-consensus"
              controlledOpen={globalOpen}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ConsensusPanel consensus={incident.consensus} />
                <GuardrailPanel review={incident.guardrail} />
              </div>
            </CollapsibleSection>
          </div>
        ) : null}

        {/* Final Recommended Action Plan */}
        {hasAnalysis && incident.final_action_plan &&
        showSection(selectedPersona, "sec-final-plan") ? (
          <div id="sec-final-plan" className="scroll-mt-32">
            <CollapsibleSection
              overline="Final plan"
              title="Final Recommended Action Plan"
              testId="section-final-plan"
              controlledOpen={globalOpen}
              defaultOpen={true}
            >
              <FinalActionPlan plan={incident.final_action_plan} />
            </CollapsibleSection>
          </div>
        ) : null}

        {/* Accountability Mapping — SLA / OLA / UC / XLA */}
        {hasAnalysis && incident.accountability &&
        showSection(selectedPersona, "sec-accountability") ? (
          <div id="sec-accountability" className="scroll-mt-32">
            <CollapsibleSection
              overline="Accountability"
              title="Accountability mapping"
              testId="section-accountability"
              controlledOpen={globalOpen}
              defaultOpen={true}
            >
              <AccountabilityPanel accountability={incident.accountability} />
            </CollapsibleSection>
          </div>
        ) : null}

        {/* Runbook */}
        {hasAnalysis && showSection(selectedPersona, "sec-runbook") ? (
          <div id="sec-runbook" className="scroll-mt-32">
            <CollapsibleSection
              overline="Runbook"
              title="Human-Reviewed Runbook Drafts"
              testId="section-runbook"
              controlledOpen={globalOpen}
            >
              <RunbookPanel
                runbook={incident.runbook}
                incidentId={incident.id}
                acks={incident.runbook_step_acks}
                onIncidentUpdate={(updated) => setIncident(updated)}
              />
            </CollapsibleSection>
          </div>
        ) : null}

        {/* Approval — flow recap + evidence package above the gate */}
        {hasAnalysis && showSection(selectedPersona, "sec-approval") ? (
          <div id="sec-approval" className="scroll-mt-32">
            <CollapsibleSection
              overline="Approval"
              title="Human Approval Required"
              testId="section-approval"
              controlledOpen={globalOpen}
              defaultOpen={true}
            >
              <div className="space-y-4">
                {incident.flow_view ? (
                  <IncidentFlowView flow={incident.flow_view} compact />
                ) : null}
                {incident.approval_evidence ? (
                  <ApprovalEvidencePackage pkg={incident.approval_evidence} />
                ) : null}
                <ApprovalGate incident={incident} onDecision={sendDecision} />
              </div>
            </CollapsibleSection>
          </div>
        ) : null}

        {/* Reviewer feedback */}
        {hasAnalysis && showSection(selectedPersona, "sec-feedback") ? (
          <div id="sec-feedback" className="scroll-mt-32">
            <CollapsibleSection
              overline="Feedback"
              title="Reviewer feedback"
              testId="section-feedback"
              controlledOpen={globalOpen}
            >
              <ReviewerFeedbackPanel
                incident={incident}
                onUpdate={(updated) => setIncident(updated)}
                onRerun={runAnalysis}
              />
            </CollapsibleSection>
          </div>
        ) : null}

        {/* MTTR */}
        {hasAnalysis && showSection(selectedPersona, "sec-mttr") ? (
          <CollapsibleSection
            overline="Post-incident"
            title="MTTR savings report"
            testId="section-mttr"
            controlledOpen={globalOpen}
          >
            <MTTRPanel
              incidentId={incident.id}
              refreshKey={incident.approval ? "approved" : "pending"}
              archived={incident.archived_pdf}
            />
          </CollapsibleSection>
        ) : null}

        {/* Audit */}
        {showSection(selectedPersona, "sec-audit") ? (
          <div id="sec-audit" className="scroll-mt-32">
            <CollapsibleSection
              overline="Audit"
              title="Audit transcript"
              testId="section-audit"
              controlledOpen={globalOpen}
            >
              <AuditTranscript events={incident.audit_trail || []} />
            </CollapsibleSection>
          </div>
        ) : null}

        <GovernanceRails />
      </div>
      <Footer />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </div>
  );
}
